import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, User, Mail, UserCheck, Building, Calendar, Shield } from 'lucide-react';
import { usuariosApi } from '../../apis/usuariosApi';

const DetalleUsuario: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: usuario, isLoading, error } = useQuery({
    queryKey: ['usuario', id],
    queryFn: () => usuariosApi.getById(Number(id)),
    enabled: !!id
  });

  const handleBack = () => {
    navigate('/usuarios');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Cargando usuario...</p>
        </div>
      </div>
    );
  }

  if (error || !usuario) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">Usuario no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .detalle-usuario-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
            color: white;
            padding: 2rem;
          }

          .content-wrapper {
            max-width: 800px;
            margin: 0 auto;
          }

          .page-header {
            text-align: left;
            margin-bottom: 3rem;
          }

          .page-title {
            font-size: 2.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
          }

          .page-subtitle {
            color: rgba(255, 255, 255, 0.7);
            font-size: 1.1rem;
          }

          .back-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            text-decoration: none;
            font-weight: 500;
            transition: all 0.3s ease;
            margin-bottom: 2rem;
            border: none;
            cursor: pointer;
          }

          .back-button:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: translateY(-2px);
          }

          .user-card {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
          }

          .user-avatar-section {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            margin-bottom: 2rem;
            padding-bottom: 2rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .user-avatar-large {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: rgba(0, 122, 255, 0.2);
            border: 2px solid rgba(0, 122, 255, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .user-info-header {
            flex: 1;
          }

          .user-name {
            font-size: 1.5rem;
            font-weight: 600;
            color: white;
            margin-bottom: 0.5rem;
          }

          .user-email {
            color: rgba(255, 255, 255, 0.7);
            font-size: 1rem;
          }

          .details-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }

          .detail-item {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .detail-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.6);
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .detail-value {
            font-size: 1rem;
            color: white;
            font-weight: 500;
          }

          .badge {
            display: inline-flex;
            align-items: center;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 0.875rem;
            font-weight: 500;
            border: 1px solid;
            width: fit-content;
          }

          .badge-blue {
            background: rgba(0, 122, 255, 0.2);
            color: #007AFF;
            border-color: rgba(0, 122, 255, 0.3);
          }

          .badge-green {
            background: rgba(52, 199, 89, 0.2);
            color: #34C759;
            border-color: rgba(52, 199, 89, 0.3);
          }

          .badge-red {
            background: rgba(255, 59, 48, 0.2);
            color: #FF3B30;
            border-color: rgba(255, 59, 48, 0.3);
          }

          .actions-section {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            padding-top: 2rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .btn {
            padding: 12px 24px;
            border-radius: 12px;
            font-weight: 500;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
            border: none;
            cursor: pointer;
            font-size: 1rem;
          }

          .btn-primary {
            background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
            color: white;
          }

          .btn-primary:hover {
            background: linear-gradient(135deg, #0056CC 0%, #003D99 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 122, 255, 0.3);
          }

          .btn-secondary {
            background: rgba(255, 255, 255, 0.1);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .btn-secondary:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          @media (max-width: 768px) {
            .details-grid {
              grid-template-columns: 1fr;
            }
            
            .user-avatar-section {
              flex-direction: column;
              text-align: center;
            }
            
            .actions-section {
              flex-direction: column;
            }
          }
        `
      }} />

      <div className="detalle-usuario-container">
        <div className="content-wrapper">
          <button onClick={handleBack} className="back-button">
            <ArrowLeft size={16} />
            Volver a usuarios
          </button>

          <div className="page-header">
            <h1 className="page-title">Detalle de Usuario</h1>
            <p className="page-subtitle">Información completa del usuario</p>
          </div>

          <div className="user-card">
            <div className="user-avatar-section">
              <div className="user-avatar-large">
                <User size={32} color="#007AFF" />
              </div>
              <div className="user-info-header">
                <div className="user-name">
                  {usuario.nombre} {usuario.apellido_paterno} {usuario.apellido_materno}
                </div>
                <div className="user-email">{usuario.correo}</div>
              </div>
            </div>

            <div className="details-grid">
              <div className="detail-item">
                <div className="detail-label">
                  <User size={16} />
                  Nombre
                </div>
                <div className="detail-value">{usuario.nombre}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">
                  <User size={16} />
                  Apellido Paterno
                </div>
                <div className="detail-value">{usuario.apellido_paterno}</div>
              </div>

              {usuario.apellido_materno && (
                <div className="detail-item">
                  <div className="detail-label">
                    <User size={16} />
                    Apellido Materno
                  </div>
                  <div className="detail-value">{usuario.apellido_materno}</div>
                </div>
              )}

              <div className="detail-item">
                <div className="detail-label">
                  <Mail size={16} />
                  Correo Electrónico
                </div>
                <div className="detail-value">{usuario.correo}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">
                  <UserCheck size={16} />
                  Rol
                </div>
                <div className="detail-value">
                  <span className="badge badge-blue">{usuario.rol.nombre}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-label">
                  <Building size={16} />
                  Plataforma
                </div>
                <div className="detail-value">
                  <span className="badge badge-green">{usuario.plataforma.nombre}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-label">
                  <Shield size={16} />
                  Estado
                </div>
                <div className="detail-value">
                  <span className={`badge ${usuario.estatus === 1 ? 'badge-green' : 'badge-red'}`}>
                    {usuario.estatus === 1 ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-label">
                  <Calendar size={16} />
                  Fecha de Creación
                </div>
                <div className="detail-value">
                  {new Date(usuario.created_at).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-label">
                  <Calendar size={16} />
                  Última Actualización
                </div>
                <div className="detail-value">
                  {new Date(usuario.updated_at).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>

            <div className="actions-section">
              <button onClick={handleBack} className="btn btn-secondary">
                Volver
              </button>
              <Link to={`/usuarios/${usuario.id}/editar`} className="btn btn-primary">
                <Edit size={16} />
                Editar Usuario
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DetalleUsuario;
